import { Link, useRouterState } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { LogOut, Moon, Sun } from 'lucide-react'
import { useTheme } from 'next-themes'
import { Mark } from '@/components/brand/logo'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { initials, useSignOut, type StaffUser } from '@/lib/session'
import { NAV } from './nav'

/** Link to a top-level section: typed route params rather than raw paths. */
export function NavTo({
  to,
  ...rest
}: {
  to: string
  className?: string
  onClick?: () => void
  children?: ReactNode
}) {
  return to === '/' ? (
    <Link to="/" {...rest} />
  ) : (
    <Link to="/$section" params={{ section: to.slice(1) }} {...rest} />
  )
}

/** Adapted from the Shadcn Space sidebar-01 block: router-aware, HCD navigation. */
export function AppSidebar({ user }: { user: StaffUser }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const { setOpenMobile } = useSidebar()
  const isActive = (to: string) => (to === '/' ? pathname === '/' : pathname.startsWith(to))

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="px-3 pt-4 pb-2">
        <Link
          to="/"
          aria-label="HCD Portal home"
          className="flex items-center gap-2.5 rounded-md px-1 py-1 text-white"
          onClick={() => setOpenMobile(false)}
        >
          <Mark className="size-8 shrink-0" />
          <span className="flex flex-col leading-tight group-data-[collapsible=icon]:hidden">
            <span className="text-[15px] font-semibold tracking-tight [font-stretch:88%]">
              HCD Portal
            </span>
            <span className="text-xs text-sidebar-foreground">Quotes and people</span>
          </span>
        </Link>
      </SidebarHeader>

      <SidebarContent className="px-1">
        {NAV.map((section, i) => (
          <SidebarGroup key={section.label ?? i} className="py-1">
            {section.label && (
              <SidebarGroupLabel className="text-[12px] font-medium text-sidebar-foreground/70">
                {section.label}
              </SidebarGroupLabel>
            )}
            <SidebarMenu>
              {section.items.map((item) => {
                const active = isActive(item.to)
                return (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton
                      isActive={active}
                      tooltip={item.title}
                      className="relative h-9 text-[14px] data-active:bg-sidebar-accent data-active:font-semibold data-active:text-white"
                      render={<NavTo to={item.to} onClick={() => setOpenMobile(false)} />}
                    >
                      {active && (
                        <span
                          aria-hidden="true"
                          className="absolute inset-y-1.5 left-0 w-[3px] rounded-full bg-sidebar-primary"
                        />
                      )}
                      <item.icon />
                      <span>{item.title}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="px-2 pb-3">
        <UserMenu user={user} />
      </SidebarFooter>
    </Sidebar>
  )
}

function UserMenu({ user }: { user: StaffUser }) {
  const signOut = useSignOut()
  const { resolvedTheme, setTheme } = useTheme()
  const dark = resolvedTheme === 'dark'

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <SidebarMenuButton size="lg" className="data-popup-open:bg-sidebar-accent">
                <Avatar className="size-8 rounded-md">
                  <AvatarFallback className="rounded-md bg-sidebar-primary text-xs font-semibold text-sidebar-primary-foreground">
                    {initials(user.name)}
                  </AvatarFallback>
                </Avatar>
                <span className="flex min-w-0 flex-col text-left leading-tight">
                  <span className="truncate text-sm font-medium text-white">{user.name}</span>
                  <span className="truncate text-xs text-sidebar-foreground">{user.email}</span>
                </span>
              </SidebarMenuButton>
            }
          />
          <DropdownMenuContent side="top" align="start" className="w-60">
            <DropdownMenuGroup>
              <DropdownMenuLabel className="font-normal">
                <span className="block text-sm font-medium text-foreground">{user.name}</span>
                <span className="block text-xs text-muted-foreground">
                  {user.roles.map(roleLabel).join(', ')}
                </span>
              </DropdownMenuLabel>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setTheme(dark ? 'light' : 'dark')}>
              {dark ? <Sun /> : <Moon />}
              {dark ? 'Use light theme' : 'Use dark theme'}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => signOut.mutate()}>
              <LogOut />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  platform_admin: 'Platform admin',
  commercial_manager: 'Commercial manager',
  sales_admin: 'Sales admin',
  hr_admin: 'HR admin',
  ops_manager: 'Operations manager',
  auditor: 'Auditor',
}
export const roleLabel = (r: string) => ROLE_LABELS[r] ?? r
